// Layouts/MediaLibrary/MediaPickerDialog.tsx
// "Choose from Media Library" — used inside the study / practical material forms.
import React, { useState, useEffect, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import MediaBrowser from './MediaBrowser';
import { MediaItem } from './mediaTypes';

interface MediaPickerDialogProps {
  visible: boolean;
  onHide: () => void;
  /** Called with the chosen files when the admin confirms */
  onConfirm: (items: MediaItem[]) => void;
  /** How many more files this material can still take (3 minus what it already has) */
  maxSelectable: number;
  /** Files already picked in the parent form — shown as selected and kept unless unticked */
  initiallySelected?: MediaItem[];
}

const MediaPickerDialog: React.FC<MediaPickerDialogProps> = ({
  visible, onHide, onConfirm, maxSelectable, initiallySelected = [],
}) => {
  const [selected, setSelected] = useState<MediaItem[]>([]);

  useEffect(() => {
    if (visible) setSelected(initiallySelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const remaining = Math.max(0, maxSelectable - selected.length);

  const toggle = useCallback((item: MediaItem) => {
    setSelected((prev) => {
      if (prev.some((p) => p.id === item.id)) return prev.filter((p) => p.id !== item.id);
      if (prev.length >= maxSelectable) return prev;
      return [...prev, item];
    });
  }, [maxSelectable]);

  // Files uploaded from inside the picker are selected straight away (duplicates included — they are the
  // existing library file, which is exactly what the admin wants to attach).
  const handleUploaded = useCallback((items: MediaItem[]) => {
    setSelected((prev) => {
      const next = [...prev];
      for (const it of items) {
        if (next.length >= maxSelectable) break;
        if (!next.some((n) => n.id === it.id)) next.push(it);
      }
      return next;
    });
  }, [maxSelectable]);

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      modal
      draggable={false}
      header={null}
      showHeader={false}
      style={{ width: '980px', maxWidth: '96vw' }}
      contentStyle={{ padding: 0, borderRadius: '20px', overflow: 'hidden' }}
      pt={{ root: { style: { borderRadius: '20px', overflow: 'hidden' } } }}
    >
      <div style={{ background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)', padding: '22px 28px', color: 'white', position: 'relative' }}>
        <button
          type="button" onClick={onHide} aria-label="Close"
          style={{ position: 'absolute', top: '16px', right: '16px', width: '32px', height: '32px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: 'white', cursor: 'pointer' }}
        >
          <i className="pi pi-times" style={{ fontSize: '0.9rem' }} />
        </button>
        <div style={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', opacity: 0.8, marginBottom: '6px' }}>Media Library</div>
        <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700 }}>Choose files</h2>
        <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)' }}>
          Pick files that are already uploaded, or upload new ones here — they are saved to the library for next time.
        </p>
      </div>

      <div style={{ padding: '20px 28px', background: '#fafafa', maxHeight: '58vh', overflowY: 'auto' }}>
        <MediaBrowser
          mode="select"
          selectedIds={selected.map((s) => s.id)}
          onToggle={toggle}
          remainingSlots={remaining}
          onUploaded={handleUploaded}
        />
      </div>

      <div style={{ padding: '14px 28px', background: 'white', borderTop: '1px solid #eef0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.82rem', color: '#475569' }}>
          <strong style={{ color: '#0f3d3e' }}>{selected.length}</strong> of {maxSelectable} selected
          {maxSelectable <= 0 && <span style={{ color: '#b45309', marginLeft: '8px' }}>— this material already has the maximum number of files</span>}
        </span>
        <div style={{ display: 'flex', gap: '10px' }}>
          <GlobalButton label="Cancel" variant="outline" onClick={onHide} />
          <GlobalButton
            label={selected.length > 0 ? `Attach ${selected.length} file${selected.length > 1 ? 's' : ''}` : 'Attach'}
            variant="primary"
            icon="pi pi-paperclip"
            onClick={() => { onConfirm(selected); onHide(); }}
            disabled={selected.length === 0 && initiallySelected.length === 0}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default MediaPickerDialog;
